import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { API_BASE } from '../../utils/api'
import { NotificationSetup } from '../../components/NotificationSetup'
import { HolidayCalendarSidebar } from '../../components/HolidayCalendarSidebar'
import type { PageView } from '../../types'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SettingsState {
  // Circuit Breaker
  cb_global_threshold: number
  cb_decision_permission: 'admin_only' | 'trader_only' | 'both'
  cb_per_set_threshold: number
  market_open_time: string
  market_close_time: string
  market_squareoff_time: string
  // Trading Allowance
  trading_override_date?: string
  trading_override_state?: boolean
}

const DEFAULTS: SettingsState = {
  cb_global_threshold: 3,
  cb_decision_permission: 'both',
  cb_per_set_threshold: 2,
  market_open_time: '09:15',
  market_close_time: '15:30',
  market_squareoff_time: '15:20',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function InfoChip({ text }: { text: string }) {
  const [show, setShow] = useState(false)
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 15, height: 15, borderRadius: '50%',
          background: 'rgba(100,116,139,0.18)', border: '1px solid var(--border)',
          fontSize: '0.5625rem', color: 'var(--text-muted)',
          cursor: 'help', fontWeight: 700, marginLeft: '0.35rem', flexShrink: 0,
        }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        ?
      </span>
      {show && (
        <span style={{
          position: 'absolute', left: '120%', top: '50%', transform: 'translateY(-50%)',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '0.4rem 0.6rem',
          fontSize: '0.6875rem', color: 'var(--text-secondary)',
          whiteSpace: 'nowrap', zIndex: 100, lineHeight: 1.4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          maxWidth: 240,
        }}>
          {text}
        </span>
      )}
    </span>
  )
}

function SectionHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
        <span style={{
          fontSize: '0.75rem', width: 26, height: 26, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(100,116,139,0.12)', borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
        }}>
          {icon}
        </span>
        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
          {title}
        </span>
      </div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0, paddingLeft: '2rem' }}>
        {subtitle}
      </p>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AdminSettings({ onNavigate }: { onNavigate?: (view: PageView) => void }) {
  const [settings, setSettings] = useState<SettingsState>(DEFAULTS)
  const [original, setOriginal] = useState<SettingsState>(DEFAULTS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const { token, session } = useAuth()
  const { addToast } = useToast()
  
  // Sidebar & Event state
  const [showCalendar, setShowCalendar] = useState(false)
  const [events, setEvents] = useState<any[]>([])

  // ── Diagnostics State & Function ──────────────────────────────────────────
  const [diagResults, setDiagResults] = useState<Record<string, { status: 'idle' | 'running' | 'pass' | 'fail' | 'warn'; ms?: number; detail?: string }>>({})
  const [diagRunning, setDiagRunning] = useState(false)

  const runDiagnostics = async () => {
    setDiagRunning(true)
    const results: typeof diagResults = {}
    
    const initCheck = (id: string) => {
      results[id] = { status: 'running' }
      setDiagResults({ ...results })
    }
    
    const setCheck = (id: string, status: 'pass' | 'fail' | 'warn', ms?: number, detail?: string) => {
      results[id] = { status, ms, detail }
      setDiagResults({ ...results })
    }

    // 1. API Worker Reachable
    const t0 = Date.now()
    initCheck('api_worker')
    try {
      const res = await fetch(`${API_BASE}/health`)
      if (res.ok) {
        setCheck('api_worker', 'pass', Date.now() - t0, 'Reachable (OK)')
      } else {
        setCheck('api_worker', 'fail', Date.now() - t0, `HTTP status ${res.status}`)
      }
    } catch (e: any) {
      setCheck('api_worker', 'fail', Date.now() - t0, e.message || 'Fetch failed')
    }

    // 2. Auth Token Validation
    initCheck('auth_token')
    if (!token) {
      setCheck('auth_token', 'fail', 0, 'No active token found')
    } else {
      try {
        const parts = token.split('.')
        if (parts.length < 3) {
          setCheck('auth_token', 'fail', 0, 'Invalid token format')
        } else {
          const payload = JSON.parse(atob(parts[1]))
          const exp = payload.exp * 1000
          if (exp > Date.now()) {
            setCheck('auth_token', 'pass', 0, `Valid (Expires in ${Math.round((exp - Date.now()) / 3600000)}h)`)
          } else {
            setCheck('auth_token', 'fail', 0, 'Token is expired')
          }
        }
      } catch (e) {
        setCheck('auth_token', 'fail', 0, 'Failed to parse token')
      }
    }

    // 3. WebSocket to MarketDataHub DO
    const t1 = Date.now()
    initCheck('ws_marketdata')
    try {
      const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const apiHost = API_BASE.replace(/^https?:\/\//, '')
      const wsUrl = `${wsProto}//${apiHost}/api/marketdata/ws`
      
      const wsPass = await new Promise<'pass' | 'fail'>((resolve) => {
        let resolved = false
        const done = (v: 'pass' | 'fail') => { if (!resolved) { resolved = true; resolve(v) } }
        
        const ws = new WebSocket(wsUrl)
        // DO cold starts can take 2-5s — allow 8s total
        const timer = setTimeout(() => {
          ws.close()
          done('fail')
        }, 8000)
        
        ws.onopen = () => {
          clearTimeout(timer)
          ws.close()
          done('pass')
        }
        ws.onerror = () => {
          clearTimeout(timer)
          done('fail')
        }
        ws.onclose = (e) => {
          clearTimeout(timer)
          // If closed before opening (code 1006 = abnormal), it's a failure
          if (!resolved) done('fail')
        }
      })
      
      if (wsPass === 'pass') {
        setCheck('ws_marketdata', 'pass', Date.now() - t1, 'WS connection established')
      } else {
        setCheck('ws_marketdata', 'fail', Date.now() - t1, 'Failed to connect to WS')
      }
    } catch (e: any) {
      setCheck('ws_marketdata', 'fail', Date.now() - t1, e.message || 'WS error')
    }

    // 4. D1 Database Read
    const t2 = Date.now()
    initCheck('d1_read')
    try {
      const res = await fetch(`${API_BASE}/api/settings`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (res.ok) {
        setCheck('d1_read', 'pass', Date.now() - t2, 'D1 settings table read OK')
      } else {
        setCheck('d1_read', 'fail', Date.now() - t2, `HTTP status ${res.status}`)
      }
    } catch (e: any) {
      setCheck('d1_read', 'fail', Date.now() - t2, e.message)
    }

    // 5. KV Store Read
    const t3 = Date.now()
    initCheck('kv_store')
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/admin`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (res.ok) {
        setCheck('kv_store', 'pass', Date.now() - t3, 'KV dashboard metrics OK')
      } else {
        setCheck('kv_store', 'fail', Date.now() - t3, `HTTP status ${res.status}`)
      }
    } catch (e: any) {
      setCheck('kv_store', 'fail', Date.now() - t3, e.message)
    }

    // 6. Durable Object Status
    initCheck('do_marketdata')
    if (results['ws_marketdata']?.status === 'pass') {
      setCheck('do_marketdata', 'pass', 0, 'DO active (broadcasting ticks)')
    } else {
      setCheck('do_marketdata', 'fail', 0, 'DO unreachable or WS failed')
    }

    // VPS and SVIS Checks
    const tVps = Date.now()
    initCheck('vps_reachable')
    initCheck('vps_engine')
    initCheck('vps_uptime')
    initCheck('vps_memory')
    initCheck('sse_clients')
    initCheck('cf_bridge')
    initCheck('broker_session')
    initCheck('broker_gcid')
    initCheck('broker_sessid')
    initCheck('broker_status')
    initCheck('iris_ws')
    initCheck('apollo_ws')
    initCheck('indices_poller')
    initCheck('indices_poll_count')
    initCheck('tick_nifty')
    initCheck('tick_banknifty')
    initCheck('tick_sensex')
    initCheck('tick_bankex')
    initCheck('tick_finnifty')
    initCheck('tick_midcp')
    initCheck('tick_vix')
    initCheck('bridge_push')

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      
      const vpsRes = await fetch('https://tunnel.la-money.co.in/health', { signal: controller.signal })
      clearTimeout(timeoutId)
      
      if (!vpsRes.ok) {
        const errMsg = `HTTP Status ${vpsRes.status}`
        const failAll = (msg: string) => {
          const ids = [
            'vps_reachable', 'vps_engine', 'vps_uptime', 'vps_memory', 'sse_clients', 'cf_bridge',
            'broker_session', 'broker_gcid', 'broker_sessid', 'broker_status', 'iris_ws', 'apollo_ws',
            'indices_poller', 'indices_poll_count', 'tick_nifty', 'tick_banknifty', 'tick_sensex',
            'tick_bankex', 'tick_finnifty', 'tick_midcp', 'tick_vix', 'bridge_push'
          ]
          ids.forEach(id => setCheck(id, 'fail', Date.now() - tVps, msg))
        }
        failAll(errMsg)
        setDiagRunning(false)
        return
      }

      const health = await vpsRes.json() as any
      const ms = Date.now() - tVps
      setCheck('vps_reachable', 'pass', ms, 'Tunnel active')

      if (health.status === 'ok') {
        setCheck('vps_engine', 'pass', ms, `Running v${health.version || '2.0.0'}`)
      } else {
        setCheck('vps_engine', 'fail', ms, `Engine status: ${health.status}`)
      }

      const hours = Math.floor((health.uptime || 0) / 3600)
      const mins = Math.floor(((health.uptime || 0) % 3600) / 60)
      setCheck('vps_uptime', 'pass', ms, `${hours}h ${mins}m`)

      const rssMB = Math.round((health.memory?.rss || 0) / (1024 * 1024))
      if (rssMB < 300) {
        setCheck('vps_memory', 'pass', ms, `${rssMB} MB (Healthy)`)
      } else if (rssMB < 500) {
        setCheck('vps_memory', 'warn', ms, `${rssMB} MB (Moderate)`)
      } else {
        setCheck('vps_memory', 'fail', ms, `${rssMB} MB (Critical limit)`)
      }

      setCheck('sse_clients', 'pass', ms, `${health.sseClients || 0} active listener(s)`)

      const lastErr = health.cfBridge?.lastError
      if (!lastErr) {
        setCheck('cf_bridge', 'pass', ms, 'Push bridge active (no errors)')
      } else {
        setCheck('cf_bridge', 'fail', ms, `Bridge error: ${lastErr}`)
      }

      const broker = health.brokers?.[0]
      if (!broker) {
        const ids = [
          'broker_session', 'broker_gcid', 'broker_sessid', 'broker_status', 'iris_ws', 'apollo_ws',
          'indices_poller', 'indices_poll_count', 'tick_nifty', 'tick_banknifty', 'tick_sensex',
          'tick_bankex', 'tick_finnifty', 'tick_midcp', 'tick_vix', 'bridge_push'
        ]
        ids.forEach(id => setCheck(id, 'fail', ms, 'No SVIS broker registered on engine'))
      } else {
        if (broker.hasSessionToken) setCheck('broker_session', 'pass', ms, 'Session JWT active')
        else setCheck('broker_session', 'fail', ms, 'No SVIS JWT token')

        if (broker.hasGcid) setCheck('broker_gcid', 'pass', ms, `Extracted GCID`)
        else setCheck('broker_gcid', 'fail', ms, 'Failed to extract GCID')

        if (broker.hasSessionId) setCheck('broker_sessid', 'pass', ms, 'IRIS session established')
        else setCheck('broker_sessid', 'fail', ms, 'Pending SVIS REST session')

        if (broker.status === 'connected') {
          setCheck('broker_status', 'pass', ms, 'SVIS connected')
        } else if (broker.status === 'error') {
          setCheck('broker_status', 'fail', ms, broker.lastError ? `Error: ${broker.lastError.substring(0, 50)}` : 'Error status')
        } else {
          setCheck('broker_status', 'warn', ms, `Status: ${broker.status}`)
        }

        if (broker.irisConnected) setCheck('iris_ws', 'pass', ms, 'Connected')
        else setCheck('iris_ws', 'warn', ms, 'Closed (pending/reconnect)')

        if (broker.apolloConnected) setCheck('apollo_ws', 'pass', ms, 'Connected')
        else setCheck('apollo_ws', 'warn', ms, 'Closed (pending/reconnect)')
      }

      const ip = health.indicesPoller
      if (!ip) {
        setCheck('indices_poller', 'fail', ms, 'Poller object missing')
        setCheck('indices_poll_count', 'fail', ms, 'No poll count')
      } else {
        if (!ip.lastError) setCheck('indices_poller', 'pass', ms, 'REST polling active (no errors)')
        else setCheck('indices_poller', 'fail', ms, `Polling error: ${ip.lastError}`)

        setCheck('indices_poll_count', 'pass', ms, `${ip.pollCount || 0} polls completed`)
      }

      const syms = health.tickSymbols || []
      const checkTick = (id: string, name: string) => {
        if (syms.includes(name)) setCheck(id, 'pass', ms, 'Ticks flowing')
        else setCheck(id, 'warn', ms, 'No ticks (market closed/unsubscribed)')
      }
      checkTick('tick_nifty', 'NIFTY')
      checkTick('tick_banknifty', 'BANKNIFTY')
      checkTick('tick_sensex', 'SENSEX')
      checkTick('tick_bankex', 'BANKEX')
      checkTick('tick_finnifty', 'FINNIFTY')
      checkTick('tick_midcp', 'MIDCPNIFTY')
      checkTick('tick_vix', 'INDIAVIX')

      const push = health.cfBridge
      if (push && push.pushCount > 0) {
        const lastPush = push.lastPushAt || 0
        const delay = Math.round((Date.now() - lastPush) / 1000)
        if (delay < 45 || lastPush > 0) {
          setCheck('bridge_push', 'pass', ms, `${push.pushCount} ticks pushed, last push ${delay}s ago`)
        } else {
          setCheck('bridge_push', 'warn', ms, `Inactive (last push ${delay}s ago)`)
        }
      } else {
        setCheck('bridge_push', 'fail', ms, 'No ticks pushed yet')
      }

    } catch (e: any) {
      const errMsg = e.message || 'Fetch failed'
      const failAll = (msg: string) => {
        const ids = [
          'vps_reachable', 'vps_engine', 'vps_uptime', 'vps_memory', 'sse_clients', 'cf_bridge',
          'broker_session', 'broker_gcid', 'broker_sessid', 'broker_status', 'iris_ws', 'apollo_ws',
          'indices_poller', 'indices_poll_count', 'tick_nifty', 'tick_banknifty', 'tick_sensex',
          'tick_bankex', 'tick_finnifty', 'tick_midcp', 'tick_vix', 'bridge_push'
        ]
        ids.forEach(id => setCheck(id, 'fail', Date.now() - tVps, msg))
      }
      failAll(errMsg)
    }

    setDiagRunning(false)
  }

  // ── Fetch on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [resSet, resEvt] = await Promise.all([
          fetch(`${API_BASE}/api/settings`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_BASE}/api/events`, { headers: { 'Authorization': `Bearer ${token}` } })
        ])
        
        if (resSet.ok) {
          const data = await resSet.json()
          const merged: SettingsState = { ...DEFAULTS, ...data }
          setSettings(merged)
          setOriginal(merged)
          if (data.updated_at) setLastUpdated(data.updated_at)
        }
        
        if (resEvt.ok) {
          setEvents(await resEvt.json())
        }
      } catch (err) {
        console.warn('Settings API unavailable, using defaults:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [token])

  // ── Dirty check ───────────────────────────────────────────────────────────
  const isDirty = JSON.stringify(settings) !== JSON.stringify(original)

  // ── Update helper ─────────────────────────────────────────────────────────
  function update<K extends keyof SettingsState>(key: K, value: SettingsState[K]) {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  // ── Trading Today Toggle ──────────────────────────────────────────────────
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
  const d = new Date(todayStr)
  const isWeekend = d.getDay() === 0 || d.getDay() === 6
  const isHoliday = events.some(e => e.date === todayStr && (e.type === 'holiday' || e.type === 'event'))
  
  const hasOverride = settings.trading_override_date === todayStr
  const isTradingToday = hasOverride ? !!settings.trading_override_state : (!isWeekend && !isHoliday)

  const handleToggleTradingToday = async (newState: boolean) => {
    update('trading_override_date', todayStr)
    update('trading_override_state', newState)
    
    try {
      await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ trading_override_date: todayStr, trading_override_state: newState }),
      })
      addToast(`Trading turned ${newState ? 'ON' : 'OFF'} for today`, 'success')
      setOriginal(prev => ({ ...prev, trading_override_date: todayStr, trading_override_state: newState }))
    } catch (err) {
      addToast('Failed to toggle trading state', 'error')
    }
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      })
      if (res.ok) {
        const data = await res.json()
        const merged = { ...DEFAULTS, ...settings, ...(data || {}) }
        setOriginal(merged)
        setSettings(merged)
        const now = new Date().toISOString()
        setLastUpdated(data?.updated_at || now)
        addToast('Settings saved successfully', 'success')
      } else {
        const err = await res.json().catch(() => ({}))
        addToast(err?.error || 'Failed to save settings', 'error')
      }
    } catch {
      addToast('Network error — could not save settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  // ── Discard ───────────────────────────────────────────────────────────────
  const handleDiscard = () => {
    setSettings(original)
    addToast('Changes discarded', 'warning')
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Page Head ──────────────────────────────────────────────────── */}
      <div className="page-head">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h1>Platform Settings</h1>
            <p className="page-sub">
              Configure system-wide behaviour — circuit breakers, market hours, and risk controls.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', paddingTop: '0.25rem' }}>
            {lastUpdated && (
              <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Last saved&nbsp;
                <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                  {new Date(lastUpdated).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: '2-digit',
                    hour: '2-digit', minute: '2-digit', hour12: false,
                  })}
                </span>
              </span>
            )}
            {isDirty && (
              <button className="btn btn-ghost btn-sm" onClick={handleDiscard} disabled={saving}>
                Discard
              </button>
            )}
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={!isDirty || saving}
              style={{ minWidth: 90 }}
            >
              {saving ? '⟳ Saving…' : '✓ Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Loading ────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>⏳ Loading settings…</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* ════════════════════════════════════════════════════════════
              Section 1: Circuit Breaker Settings
          ════════════════════════════════════════════════════════════ */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">⚡ Circuit Breaker Settings</span>
              <span style={{
                fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.06em',
                color: '#eab308', background: 'rgba(234,179,8,0.1)',
                border: '1px solid rgba(234,179,8,0.25)', borderRadius: '3px',
                padding: '0.1rem 0.45rem',
              }}>
                RISK CONTROL
              </span>
            </div>

            <div style={{ padding: '0 1rem 1.25rem' }}>
              <SectionHeader
                icon="🛡"
                title="Global Circuit Breaker"
                subtitle="Triggers a system-wide halt when total platform MTM loss exceeds this percentage of deployed capital."
              />

              <div className="cfg-grid">
                {/* Global CB Threshold */}
                <div className="cfg-field">
                  <label className="cfg-label" style={{ display: 'flex', alignItems: 'center' }}>
                    Global CB Threshold
                    <InfoChip text="When total platform MTM loss (%) exceeds this value, the circuit breaker fires and stops all running strategies." />
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        className="cfg-input"
                        type="number"
                        min={1} max={10} step={0.5}
                        value={settings.cb_global_threshold}
                        onChange={e => update('cb_global_threshold', Math.min(10, Math.max(1, Number(e.target.value))))}
                        style={{ paddingRight: '2rem' }}
                      />
                      <span style={{
                        position: 'absolute', right: '0.625rem', top: '50%', transform: 'translateY(-50%)',
                        fontSize: '0.75rem', color: 'var(--text-muted)', pointerEvents: 'none',
                      }}>%</span>
                    </div>
                    <div style={{
                      background: `rgba(${settings.cb_global_threshold <= 3 ? '138,184,42' : settings.cb_global_threshold <= 6 ? '234,179,8' : '212,73,90'},0.12)`,
                      border: `1px solid rgba(${settings.cb_global_threshold <= 3 ? '138,184,42' : settings.cb_global_threshold <= 6 ? '234,179,8' : '212,73,90'},0.3)`,
                      borderRadius: 'var(--radius-sm)',
                      padding: '0.2rem 0.5rem',
                      fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.04em',
                      color: settings.cb_global_threshold <= 3 ? '#8ab82a' : settings.cb_global_threshold <= 6 ? '#eab308' : '#d4495a',
                      whiteSpace: 'nowrap',
                    }}>
                      {settings.cb_global_threshold <= 3 ? 'CONSERVATIVE' : settings.cb_global_threshold <= 6 ? 'MODERATE' : 'AGGRESSIVE'}
                    </div>
                  </div>
                  <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', margin: '0.25rem 0 0', lineHeight: 1.4 }}>
                    Recommended: 2–4% for conservative risk profiles. Range: 1–10%.
                  </p>
                </div>

                {/* CB Decision Permission */}
                <div className="cfg-field">
                  <label className="cfg-label" style={{ display: 'flex', alignItems: 'center' }}>
                    CB Decision Permission
                    <InfoChip text="Controls who can acknowledge and reset the circuit breaker after it fires. 'First Responder' means whoever acts first — admin or trader." />
                  </label>
                  <select
                    className="cfg-input"
                    value={settings.cb_decision_permission}
                    onChange={e => update('cb_decision_permission', e.target.value as SettingsState['cb_decision_permission'])}
                  >
                    <option value="admin_only">Admin Only</option>
                    <option value="trader_only">Trader Only</option>
                    <option value="both">Admin or Trader (first responder)</option>
                  </select>
                  <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', margin: '0.25rem 0 0', lineHeight: 1.4 }}>
                    {settings.cb_decision_permission === 'admin_only'
                      ? 'Only admins can reset after CB triggers. Traders see it but cannot override.'
                      : settings.cb_decision_permission === 'trader_only'
                      ? 'Trader can self-reset their CB without waiting for admin.'
                      : 'First to act — either admin or the affected trader — can reset.'}
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid var(--border)', margin: '1.25rem 0' }} />

              <SectionHeader
                icon="🎯"
                title="Per-Set Circuit Breaker"
                subtitle="Triggers a halt for a single execution set when its individual MTM loss exceeds this percentage."
              />

              <div className="cfg-grid">
                {/* Per-set CB Threshold */}
                <div className="cfg-field">
                  <label className="cfg-label" style={{ display: 'flex', alignItems: 'center' }}>
                    Per-Set CB Threshold
                    <InfoChip text="Each execution set has its own CB threshold. When a set's MTM loss (%) hits this value, that set is squared off independently." />
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="cfg-input"
                      type="number"
                      min={1} max={10} step={0.5}
                      value={settings.cb_per_set_threshold}
                      onChange={e => update('cb_per_set_threshold', Math.min(10, Math.max(1, Number(e.target.value))))}
                      style={{ paddingRight: '2rem' }}
                    />
                    <span style={{
                      position: 'absolute', right: '0.625rem', top: '50%', transform: 'translateY(-50%)',
                      fontSize: '0.75rem', color: 'var(--text-muted)', pointerEvents: 'none',
                    }}>%</span>
                  </div>
                  <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', margin: '0.25rem 0 0', lineHeight: 1.4 }}>
                    Should be ≤ Global threshold. Default: 2%. Range: 1–10%.
                  </p>
                </div>

                {/* Validation hint */}
                <div className="cfg-field" style={{ alignSelf: 'flex-end' }}>
                  {settings.cb_per_set_threshold > settings.cb_global_threshold ? (
                    <div style={{
                      background: 'rgba(212,73,90,0.08)', border: '1px solid rgba(212,73,90,0.25)',
                      borderRadius: 'var(--radius-sm)', padding: '0.625rem 0.75rem',
                      fontSize: '0.75rem', color: '#d4495a', lineHeight: 1.5,
                    }}>
                      ⚠ Per-set threshold exceeds global — individual sets could trigger before platform-level CB.
                    </div>
                  ) : (
                    <div style={{
                      background: 'rgb(204 217 243 / 50%)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', padding: '0.625rem 0.75rem',
                      fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.5,
                    }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓</span>&nbsp;
                      Per-set ({settings.cb_per_set_threshold}%) is within global limit ({settings.cb_global_threshold}%).
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════
              Section 2: Market Hours
          ════════════════════════════════════════════════════════════ */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">🕐 Market Hours</span>
              <span style={{
                fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.06em',
                color: 'var(--accent)', background: 'rgba(56,189,248,0.08)',
                border: '1px solid rgba(56,189,248,0.2)', borderRadius: '3px',
                padding: '0.1rem 0.45rem', marginLeft: '0.5rem'
              }}>
                TIMING
              </span>
            </div>

            <div style={{ padding: '0 1rem 1.25rem' }}>
              {/* TRADING TODAY TOGGLE */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '1rem', background: 'rgba(56,189,248,0.06)', borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(56,189,248,0.15)', marginBottom: '1.25rem'
              }}>
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    Trading Today ({new Date(todayStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                    {hasOverride && (
                      <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.3rem', background: '#eab30820', color: '#eab308', borderRadius: '3px', textTransform: 'uppercase' }}>Override</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    {isWeekend ? 'Normally offline (Weekend)' : isHoliday ? 'Normally offline (Holiday)' : 'Normally online (Weekday)'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: isTradingToday ? 'var(--green)' : 'var(--text-muted)' }}>
                    {isTradingToday ? 'ON' : 'OFF'}
                  </span>
                  <button 
                    onClick={() => handleToggleTradingToday(!isTradingToday)}
                    style={{
                      width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      background: isTradingToday ? 'var(--green)' : 'rgba(255,255,255,0.1)',
                      position: 'relative', transition: 'background 0.2s', padding: 0
                    }}
                  >
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '3px', left: isTradingToday ? '23px' : '3px',
                      transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                </div>
              </div>

              <SectionHeader
                icon="📅"
                title="Session Windows"
                subtitle="Default market timing used for strategy entry gating, auto square-off, and CB activation windows."
              />

              <div className="cfg-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                {/* Market Open */}
                <div className="cfg-field">
                  <label className="cfg-label" style={{ display: 'flex', alignItems: 'center' }}>
                    Market Open Time
                    <InfoChip text="The time from which strategies are allowed to take new entries. Usually 09:15 IST for NSE." />
                  </label>
                  <input
                    className="cfg-input"
                    type="time"
                    value={settings.market_open_time}
                    onChange={e => update('market_open_time', e.target.value)}
                    style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}
                  />
                </div>

                {/* Market Close */}
                <div className="cfg-field">
                  <label className="cfg-label" style={{ display: 'flex', alignItems: 'center' }}>
                    Market Close Time
                    <InfoChip text="The official market close time. System disables new entries after this point." />
                  </label>
                  <input
                    className="cfg-input"
                    type="time"
                    value={settings.market_close_time}
                    onChange={e => update('market_close_time', e.target.value)}
                    style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}
                  />
                </div>

                {/* Square-off Time */}
                <div className="cfg-field">
                  <label className="cfg-label" style={{ display: 'flex', alignItems: 'center' }}>
                    Default Square-off Time
                    <InfoChip text="Time at which all open positions are automatically squared off if not already closed. Must be before market close." />
                  </label>
                  <input
                    className="cfg-input"
                    type="time"
                    value={settings.market_squareoff_time}
                    onChange={e => update('market_squareoff_time', e.target.value)}
                    style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}
                  />
                  {settings.market_squareoff_time >= settings.market_close_time && (
                    <p style={{ fontSize: '0.6875rem', color: '#d4495a', margin: '0.25rem 0 0' }}>
                      ⚠ Square-off time should be before market close.
                    </p>
                  )}
                </div>

                {/* Holiday Calendar Button */}
                <div className="cfg-field" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.2rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => setShowCalendar(true)}
                    style={{ 
                      width: '100%', height: '42px', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      background: 'rgba(56,189,248,0.1)', color: 'var(--accent)', border: '1px solid rgba(56,189,248,0.2)'
                    }}
                  >
                    🗓 Manage Event Calendar
                  </button>
                </div>
              </div>

              {/* Visual timeline */}
              <div style={{
                marginTop: '1rem', padding: '0.75rem 1rem',
                background: 'rgba(14,26,50,0.5)', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Session Timeline
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, position: 'relative', height: 28 }}>
                  {/* Timeline track */}
                  <div style={{
                    position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)',
                    height: 3, background: 'var(--border)', borderRadius: 99,
                  }} />
                  {/* Open dot */}
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)', border: '2px solid var(--bg-card)' }} />
                  </div>
                  <div style={{ flex: 1 }} />
                  {/* Square-off dot */}
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#eab308', border: '2px solid var(--bg-card)' }} />
                  </div>
                  <div style={{ flex: 1 }} />
                  {/* Close dot */}
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--red)', border: '2px solid var(--bg-card)' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.375rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Open</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6875rem', color: 'var(--green)', fontWeight: 700 }}>
                      {settings.market_open_time}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Square-off</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6875rem', color: '#eab308', fontWeight: 700 }}>
                      {settings.market_squareoff_time}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Close</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6875rem', color: 'var(--red)', fontWeight: 700 }}>
                      {settings.market_close_time}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════
              Section 3: Notifications
          ════════════════════════════════════════════════════════════ */}
          <NotificationSetup isAdmin={true} />

          {/* ════════════════════════════════════════════════════════════
              Section 4: System Management
          ════════════════════════════════════════════════════════════ */}
          <div style={{ marginTop: '2.5rem' }}>
            <SectionHeader 
              icon="⚙️" 
              title="System Management" 
              subtitle="Critical controls for the Trading Engine Docker container." 
            />
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>System Controls</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Restart Engine: Restarts the trading engine container. Reconnects WebSockets and restores live rates automatically in ~10 seconds.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button
                  className="btn"
                  style={{ background: '#f59e0b', color: '#000', fontWeight: 600, minWidth: '150px' }}
                  onClick={async () => {
                    if (!confirm('Restart the trading engine container? Connections will drop and auto-reconnect in ~10 seconds.')) return
                    try {
                      const res = await fetch(`${API_BASE}/api/admin/restart-engine`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${session?.token}` }
                      })
                      if (res.ok) addToast('Engine restart command sent — reconnecting in ~10s', 'success')
                      else addToast('Failed to send restart command', 'error')
                    } catch {
                      addToast('Network error', 'error')
                    }
                  }}
                >
                  🔄 Restart Engine
                </button>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════
              Section 5: System Diagnostics
          ════════════════════════════════════════════════════════════ */}
          <div style={{ marginTop: '2.5rem' }}>
            <SectionHeader 
              icon="🔍" 
              title="System Diagnostics" 
              subtitle="Run automated connectivity and data checks across all platform layers." 
            />
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Connection Verification</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Runs 27 checks covering the Browser, Cloudflare Edge, Hostinger VPS, Greeksoft API, and Live Market Data streams.
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={runDiagnostics}
                  disabled={diagRunning}
                  style={{ minWidth: '150px' }}
                >
                  {diagRunning ? '⏳ Testing...' : '🔍 Run Diagnostics'}
                </button>
              </div>

              {Object.keys(diagResults).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {/* Summary Bar */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '0.75rem 1rem', background: 'rgba(100,116,139,0.08)',
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                    fontSize: '0.8125rem'
                  }}>
                    <span style={{ fontWeight: 600 }}>Results Summary:</span>
                    <span style={{ color: '#10b981' }}>
                      ✅ {Object.values(diagResults).filter(r => r.status === 'pass').length} Passed
                    </span>
                    {Object.values(diagResults).filter(r => r.status === 'warn').length > 0 && (
                      <span style={{ color: '#f59e0b' }}>
                        ⚠️ {Object.values(diagResults).filter(r => r.status === 'warn').length} Warning(s)
                      </span>
                    )}
                    {Object.values(diagResults).filter(r => r.status === 'fail').length > 0 && (
                      <span style={{ color: '#ef4444' }}>
                        ❌ {Object.values(diagResults).filter(r => r.status === 'fail').length} Failed
                      </span>
                    )}
                  </div>

                  {/* Diagnostic Groups */}
                  {[
                    { title: 'Layer 1: Client & Browser', group: 'frontend_worker', ids: ['api_worker', 'auth_token', 'ws_marketdata'] },
                    { title: 'Layer 2: Cloudflare Edge Network', group: 'cloudflare', ids: ['d1_read', 'kv_store', 'do_marketdata'] },
                    { title: 'Layer 3: Hostinger VPS Container', group: 'vps_engine', ids: ['vps_reachable', 'vps_engine', 'vps_uptime', 'vps_memory', 'sse_clients', 'cf_bridge'] },
                    { title: 'Layer 4: Greeksoft SVIS Broker', group: 'broker_svis', ids: ['broker_session', 'broker_gcid', 'broker_sessid', 'broker_status', 'iris_ws', 'apollo_ws'] },
                    { title: 'Layer 4: Greeksoft Market Data REST Poller', group: 'indices_poller', ids: ['indices_poller', 'indices_poll_count'] },
                    { title: 'Live Watch Tick Feed Flow', group: 'market_data', ids: ['tick_nifty', 'tick_banknifty', 'tick_sensex', 'tick_bankex', 'tick_finnifty', 'tick_midcp', 'tick_vix', 'bridge_push'] }
                  ].map((cat, idx) => (
                    <div key={idx}>
                      <div style={{
                        fontSize: '0.6875rem', fontWeight: 800, textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: 'var(--text-muted)',
                        paddingBottom: '0.35rem', borderBottom: '1px solid var(--border)',
                        marginBottom: '0.5rem'
                      }}>
                        {cat.title}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {cat.ids.map(id => {
                          const r = diagResults[id] || { status: 'idle' }
                          let icon = '⚪'
                          let color = 'var(--text-muted)'
                          let statusText = 'Not checked'
                          
                          if (r.status === 'running') {
                            icon = '⏳'
                            color = '#3b82f6'
                            statusText = 'Checking...'
                          } else if (r.status === 'pass') {
                            icon = '✅'
                            color = '#10b981'
                            statusText = 'PASSED'
                          } else if (r.status === 'fail') {
                            icon = '❌'
                            color = '#ef4444'
                            statusText = 'FAILED'
                          } else if (r.status === 'warn') {
                            icon = '⚠️'
                            color = '#f59e0b'
                            statusText = 'WARNING'
                          }

                          // Check names mapping
                          const checkNames: Record<string, string> = {
                            api_worker: 'API Worker Reachable',
                            auth_token: 'Superadmin JWT Validation',
                            ws_marketdata: 'WebSocket Connection to DO',
                            d1_read: 'D1 Database settings Query',
                            kv_store: 'KV Store Admin Metrics Query',
                            do_marketdata: 'MarketDataHub DO Status',
                            vps_reachable: 'VPS Zero-Trust Tunnel',
                            vps_engine: 'VPS Trading Engine App',
                            vps_uptime: 'Engine Uptime',
                            vps_memory: 'VPS Memory Usage',
                            sse_clients: 'SSE Broadcast Listeners',
                            cf_bridge: 'VPS-Cloudflare Bridge Configuration',
                            broker_session: 'SVIS Session Token',
                            broker_gcid: 'Greeksoft GCID Extracted',
                            broker_sessid: 'SVIS Session ID (IRIS)',
                            broker_status: 'Greeksoft Broker Client Connection',
                            iris_ws: 'IRIS WebSocket (Orders Feed)',
                            apollo_ws: 'Apollo WebSocket (Live Ticks Feed)',
                            indices_poller: 'Backup Indices REST Poller',
                            indices_poll_count: 'REST Poll Requests Count',
                            tick_nifty: 'NIFTY Index Spot Ticks',
                            tick_banknifty: 'BANKNIFTY Index Spot Ticks',
                            tick_sensex: 'BSE SENSEX Index Spot Ticks',
                            tick_bankex: 'BSE BANKEX Index Spot Ticks',
                            tick_finnifty: 'FINNIFTY Index Spot Ticks',
                            tick_midcp: 'MIDCPNIFTY Index Spot Ticks',
                            tick_vix: 'INDIAVIX Volatility Index Spot Ticks',
                            bridge_push: 'Continuous Tick Pushes from VPS to Cloudflare'
                          }

                          return (
                            <div key={id} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.02)',
                              fontSize: '0.75rem', flexWrap: 'wrap', gap: '0.5rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '220px' }}>
                                <span>{icon}</span>
                                <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
                                  {checkNames[id] || id}
                                </span>
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                {r.detail && (
                                  <span style={{ color: r.status === 'fail' ? '#ef4444' : r.status === 'warn' ? '#f59e0b' : 'var(--text-muted)', fontSize: '0.6875rem' }}>
                                    {r.detail}
                                  </span>
                                )}
                                {r.ms !== undefined && r.ms > 0 && (
                                  <span style={{ color: 'var(--text-muted)', fontSize: '0.625rem', fontFamily: 'monospace' }}>
                                    {r.ms}ms
                                  </span>
                                )}
                                <span style={{
                                  fontSize: '0.5625rem', fontWeight: 800, padding: '0.1rem 0.35rem',
                                  borderRadius: '2px', background: `${color}18`, border: `1px solid ${color}44`,
                                  color, letterSpacing: '0.05em'
                                }}>
                                  {statusText}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Sticky save footer (shows when dirty) ────────────────── */}
          {isDirty && (
            <div style={{
              position: 'sticky', bottom: '1rem',
              display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              zIndex: 10,
            }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', alignSelf: 'center', marginRight: 'auto' }}>
                ● Unsaved changes
              </span>
              <button className="btn btn-ghost btn-sm" onClick={handleDiscard} disabled={saving}>
                Discard
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ minWidth: 100 }}>
                {saving ? '⟳ Saving…' : '✓ Save Changes'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Holiday Calendar Sidebar */}
      {showCalendar && (
        <HolidayCalendarSidebar onClose={() => setShowCalendar(false)} />
      )}
    </div>
  )
}
